/**
 * Workout Player Page (Client) - Epic 4
 * Mobile-first workout tracking interface
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Play,
  Pause,
  Check,
  Timer,
  Dumbbell,
  ChevronDown,
  ChevronUp,
  Link2,
  Loader2,
  X,
  Trophy,
  AlertCircle,
} from "lucide-react";
import {
  useWorkoutPlayer,
  startWorkoutSession,
  logSet,
  finishWorkoutSession,
} from "../hooks/useSession";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Toaster, toast } from "sonner";

// RPE scale labels
const RPE_LABELS = {
  1: "Très facile",
  2: "Facile",
  3: "Modéré",
  4: "Un peu dur",
  5: "Dur",
  6: "Difficile",
  7: "Très dur",
  8: "Extrêmement dur",
  9: "Quasi max",
  10: "Effort maximal",
};

// Rest timer component
function RestTimer({ seconds, onComplete }) {
  const [remaining, setRemaining] = useState(seconds);
  const [isRunning, setIsRunning] = useState(false);
  
  useEffect(() => {
    if (!isRunning || remaining <= 0) return;
    
    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          setIsRunning(false);
          onComplete?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isRunning, remaining, onComplete]);
  
  const toggleTimer = () => {
    if (remaining <= 0) {
      setRemaining(seconds);
    }
    setIsRunning(!isRunning);
  };
  
  const formatTime = (s) => {
    const mins = Math.floor(s / 60);
    const secs = s % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  
  const progress = ((seconds - remaining) / seconds) * 100;
  
  return (
    <button
      onClick={toggleTimer}
      className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
        isRunning
          ? "bg-lime-400 text-zinc-950"
          : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
      }`}
    >
      {isRunning ? (
        <Pause className="w-4 h-4" />
      ) : (
        <Timer className="w-4 h-4" />
      )}
      <span className="font-mono font-bold">{formatTime(remaining)}</span>
      {isRunning && (
        <div className="w-12 h-1 bg-zinc-950/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-zinc-950 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </button>
  );
}

// Set row component
function SetRow({ set, exerciseId, sessionId, loggedSets, onLog }) {
  const [reps, setReps] = useState(set.target_reps.toString());
  const [weight, setWeight] = useState(set.target_weight_kg?.toString() || "");
  const [saving, setSaving] = useState(false);
  
  // Check if this set is already logged
  const logged = loggedSets?.find(
    (ls) => ls.exercise_id === exerciseId && ls.set_number === set.set_number
  );
  
  const isLogged = !!logged;
  
  useEffect(() => {
    if (logged) {
      setReps(logged.actual_reps.toString());
      setWeight(logged.actual_weight_kg?.toString() || "");
    }
  }, [logged]);
  
  const handleSave = async () => {
    if (!sessionId) return;
    
    setSaving(true);
    try {
      await logSet(sessionId, {
        exercise_id: exerciseId,
        template_set_id: set.id,
        set_number: set.set_number,
        actual_reps: parseInt(reps) || set.target_reps,
        actual_weight_kg: weight ? parseFloat(weight) : null,
      });
      onLog?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${
        isLogged ? "bg-lime-400/10 border border-lime-400/30" : "bg-zinc-900"
      }`}
    >
      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
        {isLogged ? (
          <Check className="w-4 h-4 text-lime-400" />
        ) : (
          <span className="text-zinc-400 font-bold text-sm">{set.set_number}</span>
        )}
      </div>
      
      <div className="flex-1 flex items-center gap-2">
        <div className="flex-1">
          <Input
            type="number"
            value={reps}
            onChange={(e) => setReps(e.target.value)}
            className="bg-zinc-800 border-zinc-700 h-10 text-center font-bold"
            placeholder={set.target_reps.toString()}
          />
          <p className="text-xs text-zinc-600 text-center mt-1">reps</p>
        </div>
        
        <span className="text-zinc-600">@</span>
        
        <div className="flex-1">
          <Input
            type="number"
            step="0.5"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            className="bg-zinc-800 border-zinc-700 h-10 text-center font-bold"
            placeholder={set.target_weight_kg?.toString() || "-"}
          />
          <p className="text-xs text-zinc-600 text-center mt-1">kg</p>
        </div>
      </div>
      
      <Button
        onClick={handleSave}
        disabled={saving}
        size="icon"
        className={`h-10 w-10 rounded-full ${
          isLogged
            ? "bg-lime-400/20 text-lime-400 hover:bg-lime-400/30"
            : "bg-lime-400 text-zinc-950 hover:bg-lime-500"
        }`}
      >
        {saving ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Check className="w-4 h-4" />
        )}
      </Button>
    </div>
  );
}

// Exercise card component
function ExerciseCard({ templateExercise, sessionId, loggedSets, isSuperset, supersetPartner, onLog }) {
  const [expanded, setExpanded] = useState(true);
  const exercise = templateExercise.exercise;
  const sets = templateExercise.sets || [];
  
  // Count completed sets
  const completedSets = sets.filter((set) =>
    loggedSets?.some(
      (ls) => ls.exercise_id === exercise.id && ls.set_number === set.set_number
    )
  ).length;
  
  const progress = sets.length > 0 ? (completedSets / sets.length) * 100 : 0;
  
  return (
    <Card
      className={`bg-zinc-900/50 border-zinc-800 overflow-hidden ${
        isSuperset ? "border-l-4 border-l-sky-400" : ""
      }`}
    >
      <CardContent className="p-0">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 bg-zinc-800 rounded-xl flex items-center justify-center flex-shrink-0">
            <Dumbbell className="w-6 h-6 text-lime-400" />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-white truncate">{exercise.name}</h3>
              {isSuperset && (
                <Badge className="bg-sky-400/10 text-sky-400 border-sky-400/30 text-xs">
                  <Link2 className="w-3 h-3 mr-1" />
                  Superset {templateExercise.superset_group}
                </Badge>
              )}
            </div>
            
            <div className="flex items-center gap-2 mt-1">
              <span className="text-zinc-500 text-sm">
                {completedSets}/{sets.length} séries
              </span>
              <Progress value={progress} className="flex-1 h-1" />
            </div>
          </div>
          
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-zinc-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-500" />
          )}
        </button>
        
        {/* Content */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-4 space-y-3">
                {/* Coach notes */}
                {templateExercise.coach_notes && (
                  <div className="p-3 bg-amber-400/10 rounded-lg border border-amber-400/30">
                    <p className="text-amber-400 text-sm">
                      💡 {templateExercise.coach_notes}
                    </p>
                  </div>
                )}
                
                {/* Sets */}
                {sets.map((set) => (
                  <SetRow
                    key={set.id}
                    set={set}
                    exerciseId={exercise.id}
                    sessionId={sessionId}
                    loggedSets={loggedSets}
                    onLog={onLog}
                  />
                ))}
                
                {/* Rest timer (only show if not last exercise or has rest) */}
                {templateExercise.rest_seconds > 0 && (
                  <div className="flex justify-center pt-2">
                    <RestTimer
                      seconds={templateExercise.rest_seconds}
                      onComplete={() => toast.info("⏰ Repos terminé !")}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}

export default function WorkoutPlayerPage() {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const { workout, isLoading, isError, mutate } = useWorkoutPlayer(workoutId);
  
  const [sessionId, setSessionId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [selectedRpe, setSelectedRpe] = useState(7);
  const [feedback, setFeedback] = useState("");
  const [finishing, setFinishing] = useState(false);
  
  // Set session ID when workout loads
  useEffect(() => {
    if (workout?.session?.id) {
      setSessionId(workout.session.id);
    }
  }, [workout]);
  
  // Start session
  const handleStart = async () => {
    setStarting(true);
    try {
      const session = await startWorkoutSession(workoutId);
      setSessionId(session.id);
      mutate();
      toast.success("🏋️ C'est parti !");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStarting(false);
    }
  };
  
  // Finish session
  const handleFinish = async () => {
    if (!sessionId) return;
    
    setFinishing(true);
    try {
      await finishWorkoutSession(sessionId, {
        overall_rpe: selectedRpe,
        client_feedback: feedback || null,
      });
      toast.success("🎉 Séance terminée !");
      setFinishDialogOpen(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setFinishing(false);
    }
  };
  
  // Calculate progress
  const loggedSets = workout?.session?.logged_sets || [];
  const totalSets = workout?.exercises?.reduce((acc, te) => acc + te.sets.length, 0) || 0;
  const completedSets = loggedSets.length;
  const overallProgress = totalSets > 0 ? (completedSets / totalSets) * 100 : 0;
  
  // Group exercises by superset
  const groupedExercises = [];
  if (workout?.exercises) {
    let currentGroup = null;
    
    for (const te of workout.exercises) {
      if (te.superset_group) {
        if (currentGroup && currentGroup.group === te.superset_group) {
          currentGroup.exercises.push(te);
        } else {
          if (currentGroup) groupedExercises.push(currentGroup);
          currentGroup = { group: te.superset_group, exercises: [te] };
        }
      } else {
        if (currentGroup) {
          groupedExercises.push(currentGroup);
          currentGroup = null;
        }
        groupedExercises.push({ group: null, exercises: [te] });
      }
    }
    
    if (currentGroup) groupedExercises.push(currentGroup);
  }
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-lime-400" />
      </div>
    );
  }
  
  if (isError || !workout) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
        <Card className="bg-zinc-900/50 border-zinc-800 max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <p className="text-zinc-400 mb-4">Séance non trouvée</p>
            <Button onClick={() => navigate("/dashboard")} variant="outline">
              Retour au dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const isSessionStarted = !!sessionId;
  const isSessionFinished = workout.session?.finished_at;
  
  return (
    <div className="min-h-screen bg-zinc-950 pb-32">
      <Toaster position="top-center" richColors />
      <div className="noise-overlay" />
      
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-zinc-800">
        <div className="max-w-lg mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            
            <div className="text-center flex-1">
              <h1 className="font-heading font-bold text-white truncate px-2">
                {workout.template_name}
              </h1>
            </div>
            
            <div className="w-10" /> {/* Spacer */}
          </div>
          
          {/* Progress bar */}
          {isSessionStarted && !isSessionFinished && (
            <div className="pb-2">
              <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
                <span>{completedSets} / {totalSets} séries</span>
                <span>{Math.round(overallProgress)}%</span>
              </div>
              <Progress value={overallProgress} className="h-2" />
            </div>
          )}
        </div>
      </header>
      
      {/* Main Content */}
      <main className={`max-w-lg mx-auto px-4 ${isSessionStarted ? "pt-24" : "pt-20"}`}>
        {/* Not started state */}
        {!isSessionStarted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="py-8"
          >
            <Card className="bg-gradient-to-br from-lime-400/10 to-lime-400/5 border-lime-400/20">
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-lime-400/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Dumbbell className="w-10 h-10 text-lime-400" />
                </div>
                
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  {workout.template_name}
                </h2>
                
                {workout.template_description && (
                  <p className="text-zinc-400 text-sm mb-4">
                    {workout.template_description}
                  </p>
                )}
                
                <div className="flex justify-center gap-4 text-sm text-zinc-500 mb-6">
                  <span>{workout.exercises?.length || 0} exercices</span>
                  <span>•</span>
                  <span>{totalSets} séries</span>
                </div>
                
                <Button
                  onClick={handleStart}
                  disabled={starting}
                  data-testid="start-session-button"
                  className="w-full h-14 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full text-lg"
                >
                  {starting ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      Commencer
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
        
        {/* Session in progress */}
        {isSessionStarted && !isSessionFinished && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4 py-4"
          >
            {groupedExercises.map((group, idx) => (
              <div key={idx} className="space-y-2">
                {group.group && group.exercises.length > 1 && (
                  <div className="flex items-center gap-2 px-2">
                    <Link2 className="w-4 h-4 text-sky-400" />
                    <span className="text-sky-400 text-sm font-medium">
                      Superset {group.group}
                    </span>
                    <div className="flex-1 h-px bg-sky-400/30" />
                  </div>
                )}
                
                {group.exercises.map((te) => (
                  <ExerciseCard
                    key={te.id}
                    templateExercise={te}
                    sessionId={sessionId}
                    loggedSets={loggedSets}
                    isSuperset={!!group.group}
                    onLog={mutate}
                  />
                ))}
              </div>
            ))}
          </motion.div>
        )}
        
        {/* Session finished */}
        {isSessionFinished && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="py-8"
          >
            <Card className="bg-gradient-to-br from-lime-400/20 to-lime-400/5 border-lime-400/30">
              <CardContent className="p-6 text-center">
                <div className="w-20 h-20 bg-lime-400/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-lime-400" />
                </div>
                
                <h2 className="font-heading text-2xl font-bold text-white mb-2">
                  Séance terminée !
                </h2>
                
                <p className="text-zinc-400 mb-4">
                  RPE: {workout.session.overall_rpe}/10
                </p>
                
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold rounded-full"
                >
                  Retour au dashboard
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
      
      {/* Fixed Footer - Finish button */}
      {isSessionStarted && !isSessionFinished && (
        <div className="fixed bottom-0 left-0 right-0 p-4 glass border-t border-zinc-800">
          <div className="max-w-lg mx-auto">
            <Button
              onClick={() => setFinishDialogOpen(true)}
              data-testid="finish-session-button"
              className="w-full h-14 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              <Check className="w-5 h-5 mr-2" />
              Terminer la séance
            </Button>
          </div>
        </div>
      )}
      
      {/* Finish Dialog */}
      <Dialog open={finishDialogOpen} onOpenChange={setFinishDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white text-center">
              🎉 Bien joué !
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-center">
              Comment s'est passée cette séance ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* RPE Selector */}
            <div className="space-y-3">
              <label className="text-zinc-300 text-sm font-medium">
                Difficulté ressentie (RPE)
              </label>
              
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={selectedRpe}
                  onChange={(e) => setSelectedRpe(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-lime-400"
                />
                <span className="w-8 text-center font-bold text-lime-400 text-xl">
                  {selectedRpe}
                </span>
              </div>
              
              <p className="text-center text-zinc-500 text-sm">
                {RPE_LABELS[selectedRpe]}
              </p>
            </div>
            
            {/* Feedback */}
            <div className="space-y-2">
              <label className="text-zinc-300 text-sm font-medium">
                Commentaire (optionnel)
              </label>
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Comment vous sentez-vous ?"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            
            {/* Submit */}
            <Button
              onClick={handleFinish}
              disabled={finishing}
              data-testid="confirm-finish-button"
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-zinc-950 font-bold uppercase tracking-wider rounded-full"
            >
              {finishing ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Valider"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
